import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AlertBanner, Button, Spinner } from '@rentflowdev/ui'
import { MessageCircleIcon } from 'lucide-react'
import {
	useDisconnectPlatformMutation,
	useGetFeedUrlQuery,
	useLazyGetAvitoOauthUrlQuery,
	useListConnectionsQuery,
} from '@/api'
import { IntegrationCard } from '@/components/integration-card'
import { AvitoImportDialog } from '@/components/avito-import-dialog'
import { CianConnectDialog } from '@/components/cian-connect-dialog'
import { TelegramConnectDialog } from '@/components/telegram-connect-dialog'
import type { IntegrationPlatform, PlatformConnection } from '@/types'

export const Route = createFileRoute('/_app/settings/integrations')({
	component: SettingsIntegrationsPage,
})

type Filter = 'all' | 'payments' | 'messengers' | 'platforms'

interface IntegrationDef {
	id:           'AVITO' | 'CIAN' | 'TELEGRAM' | 'YOOKASSA'
	platform?:    IntegrationPlatform   // null for non-platform integrations (Telegram/YooKassa)
	category:     'platforms' | 'messengers' | 'payments'
	host?:        string
	hostHref?:    string
	logoSrc?:     string
	fallbackIcon?: typeof MessageCircleIcon
	fallbackBg?: string
}

const INTEGRATIONS: IntegrationDef[] = [
	{ id: 'AVITO',    platform: 'AVITO', category: 'platforms',  host: 'avito.ru',     hostHref: 'https://avito.ru',     logoSrc: '/logos/avito_integration.png' },
	{ id: 'CIAN',     platform: 'CIAN',  category: 'platforms',  host: 'cian.ru',      hostHref: 'https://cian.ru',      logoSrc: '/logos/cian_integration.png' },
	{ id: 'TELEGRAM', platform: 'TELEGRAM', category: 'messengers', host: 'telegram.org', hostHref: 'https://telegram.org', logoSrc: '/logos/telegram.png' },
	{ id: 'YOOKASSA',                    category: 'payments',   host: 'yookassa.ru',  hostHref: 'https://yookassa.ru',  logoSrc: '/logos/ukassa.png' },
]

function PlatformLogo({ src, alt, fallbackIcon: Icon, fallbackBg }: {
	src?:          string
	alt:           string
	fallbackIcon?: typeof MessageCircleIcon
	fallbackBg?:   string
}) {
	if (src) {
		return <img src={src} alt={alt} className="size-10 rounded-(--radius-md) object-cover" />
	}
	if (Icon) {
		return (
			<div className={`grid size-10 place-items-center rounded-(--radius-md) text-white ${fallbackBg ?? 'bg-(--color-muted)'}`}>
				<Icon className="size-5" />
			</div>
		)
	}
	return <div className="size-10 rounded-(--radius-md) bg-(--color-muted)" />
}

function SettingsIntegrationsPage() {
	const { t } = useTranslation()
	const [filter, setFilter] = useState<Filter>('all')
	const [cianOpen, setCianOpen] = useState(false)
	const [telegramOpen, setTelegramOpen] = useState(false)
	const [avitoImportConnectionId, setAvitoImportConnectionId] = useState<
		string | null
	>(null)

	const { data: connectionsData, isLoading } = useListConnectionsQuery()
	const [getAvitoOauthUrl, { isFetching: isAvitoConnecting }] = useLazyGetAvitoOauthUrlQuery()
	const [disconnect] = useDisconnectPlatformMutation()

	const connections = connectionsData?.connections ?? []
	const connectionByPlatform = useMemo(() => {
		const map = new Map<IntegrationPlatform, PlatformConnection>()
		for (const c of connections) if (c.isActive) map.set(c.platform, c)
		return map
	}, [connections])

	const cianConnected  = !!connectionByPlatform.get('CIAN')
	const { data: cianFeed  } = useGetFeedUrlQuery('CIAN',  { skip: !cianConnected  })

	const visible = filter === 'all'
		? INTEGRATIONS
		: INTEGRATIONS.filter((i) => i.category === filter)

	const [connectError, setConnectError] = useState<string | null>(null)

	async function handleAvitoConnect() {
		setConnectError(null)
		try {
			const result = await getAvitoOauthUrl().unwrap()
			window.location.href = result.authorizationUrl
		} catch (err) {
			console.error('getAvitoOauthUrl failed:', err)
			const data = (err as { data?: { message?: string | string[] } })?.data
			const message = Array.isArray(data?.message) ? data.message.join(', ') : data?.message
			setConnectError(message ?? t('settings.integrations.errors.avitoConnect'))
		}
	}

	function handleAvitoDisconnect() {
		void disconnect('AVITO')
	}

	function handleCianDisconnect() {
		void disconnect('CIAN')
	}

	function handleTelegramDisconnect() {
		void disconnect('TELEGRAM')
	}

	const filters: { id: Filter; label: string }[] = [
		{ id: 'all',        label: t('settings.integrations.filters.all') },
		{ id: 'payments',   label: t('settings.integrations.filters.payments') },
		{ id: 'messengers', label: t('settings.integrations.filters.messengers') },
		{ id: 'platforms',  label: t('settings.integrations.filters.platforms') },
	]

	return (
		<div className="flex flex-col gap-4 px-4 py-4">
			<div className="flex flex-wrap gap-2">
				{filters.map((f) => (
					<Button
						key={f.id}
						size="sm"
						variant={filter === f.id ? 'default' : 'secondary'}
						onClick={() => setFilter(f.id)}
					>
						{f.label}
					</Button>
				))}
			</div>

			{connectError && (
				<AlertBanner variant="destructive" title={t('settings.integrations.errors.title')} description={connectError} />
			)}

			{isLoading ? (
				<Spinner size="sm" />
			) : (
				<div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
					{visible.map((def) => {
						const conn = def.platform ? connectionByPlatform.get(def.platform) : undefined
						const connected = !!conn

						const logo = (
							<PlatformLogo
								src={def.logoSrc}
								alt={t(`settings.integrations.${def.id.toLowerCase()}.name`)}
								fallbackIcon={def.fallbackIcon}
								fallbackBg={def.fallbackBg}
							/>
						)
						const description = t(`settings.integrations.${def.id.toLowerCase()}.description`)
						const name = t(`settings.integrations.${def.id.toLowerCase()}.name`)

						switch (def.id) {
							case 'AVITO':
								return (
									<IntegrationCard
										key={def.id}
										logo={logo}
										name={name}
										host={def.host}
										hostHref={def.hostHref}
										description={description}
										connected={connected}
										connectedAs={conn?.externalAccountName}
										itemsCount={conn?.itemsCount}
										primaryLabel={connected ? t('settings.integrations.actions.reconnect') : t('settings.integrations.actions.connect')}
										secondaryLabel={connected ? t('settings.integrations.actions.disconnect') : undefined}
										onPrimary={() => void handleAvitoConnect()}
										onSecondary={connected ? handleAvitoDisconnect : undefined}
										disabled={isAvitoConnecting}
										extraAction={
											connected && conn ? (
												<Button
													size="sm"
													variant="secondary"
													onClick={() => setAvitoImportConnectionId(conn.id)}
												>
													{t('settings.integrations.actions.import')}
												</Button>
											) : undefined
										}
									/>
								)
							case 'CIAN':
								return (
									<IntegrationCard
										key={def.id}
										logo={logo}
										name={name}
										host={def.host}
										hostHref={def.hostHref}
										description={description}
										connected={connected}
										connectedAs={conn?.externalAccountName}
										primaryLabel={connected ? t('settings.integrations.actions.disconnect') : t('settings.integrations.actions.configure')}
										onPrimary={connected ? handleCianDisconnect : () => setCianOpen(true)}
										feedUrl={cianFeed?.url}
									/>
								)
							case 'TELEGRAM':
								return (
									<IntegrationCard
										key={def.id}
										logo={logo}
										name={name}
										host={def.host}
										hostHref={def.hostHref}
										description={description}
										connected={connected}
										connectedAs={conn?.externalAccountName}
										primaryLabel={connected ? t('settings.integrations.actions.reconnect') : t('settings.integrations.actions.connect')}
										secondaryLabel={connected ? t('settings.integrations.actions.disconnect') : undefined}
										onPrimary={() => setTelegramOpen(true)}
										onSecondary={connected ? handleTelegramDisconnect : undefined}
									/>
								)
							case 'YOOKASSA':
								return (
									<IntegrationCard
										key={def.id}
										logo={logo}
										name={name}
										host={def.host}
										hostHref={def.hostHref}
										description={description}
										primaryLabel={t('settings.integrations.actions.configure')}
										onPrimary={() => undefined}
										disabled
									/>
								)
						}
					})}
				</div>
			)}


			<CianConnectDialog open={cianOpen} onOpenChange={setCianOpen} />
			<TelegramConnectDialog open={telegramOpen} onOpenChange={setTelegramOpen} connectedBotName={connectionByPlatform.get('TELEGRAM')?.externalAccountName} />
			{avitoImportConnectionId && (
				<AvitoImportDialog
					connectionId={avitoImportConnectionId}
					open={!!avitoImportConnectionId}
					onOpenChange={(o) => {
						if (!o) setAvitoImportConnectionId(null)
					}}
				/>
			)}
		</div>
	)
}
